'use client';

import { observer } from 'mobx-react-lite';
import { useEffect, type ComponentType } from 'react';
import { useRouter } from 'next/navigation';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useRepScoreStore, useAuthStore } from '@/presentation/hooks/useStores';
import type { RepScoreViewState } from '@/shared/types/RepScoreTypes';
import { cn } from '@/libs/utils';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';
import { useResponsiveSheetSide } from '@/presentation/hooks/useResponsiveSheetSide';

import {
  AnonymousState,
  NewUserState,
  ProgressingState,
  HighTrustState,
  AtRiskState,
} from './repScore/states';

interface RepScoreDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Props passed to each state component. Kept loose so shared wiring works for
 * every state; individual states use only the props they need.
 */
interface StateComponentProps {
  onPayNow?: () => void;
  onStartPlan?: () => void;
}

const STATE_COMPONENTS: Record<
  RepScoreViewState,
  ComponentType<StateComponentProps>
> = {
  new_user: NewUserState,
  progressing: ProgressingState,
  high_trust: HighTrustState,
  at_risk: AtRiskState,
};

/**
 * Early Pickup drawer.
 *
 * Renders one of four state components based on the user's current RepScore.
 * Mirrors Pencil frame g5NrLX: header with title + subtitle, close control,
 * and the state body. No gradient, no decorative illustration.
 */
const RepScoreDrawer = observer(function RepScoreDrawer({
  open,
  onOpenChange,
}: RepScoreDrawerProps) {
  const repScoreStore = useRepScoreStore();
  const authStore = useAuthStore();
  const side = useResponsiveSheetSide();
  const router = useRouter();

  useEffect(() => {
    if (open && authStore.isAuthenticated) {
      trackConsumerEvent({
        eventType: 'repscore_opened',
        properties: {
          source: 'header_drawer',
          score_level: repScoreStore.scoreLevel || 'unknown',
        },
      });
      const userId = authStore.currentUser?.id || 'me';
      void repScoreStore.loadUserRepScore(userId);
    }
  }, [open, authStore.isAuthenticated, authStore.currentUser?.id, repScoreStore]);

  const isAuthenticated = authStore.isAuthenticated;
  const viewState = repScoreStore.viewState;
  const StateComponent = STATE_COMPONENTS[viewState];

  const handlePayNow = () => {
    onOpenChange(false);
    router.push('/order');
  };

  const handleStartPlan = () => {
    onOpenChange(false);
  };

  const handleAnonBeforeNavigate = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          'flex w-full flex-col gap-0 overflow-y-auto bg-white p-0',
          // Restyle shadcn's built-in close button to match Pencil: 36x36 rounded pill with motitoBlack X
          '[&>button]:right-5 [&>button]:top-5 [&>button]:flex [&>button]:size-9 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-[#F3F4F6] [&>button]:opacity-100 [&>button]:ring-motitoBlue',
          '[&>button>svg]:size-[18px] [&>button>svg]:text-[#0C0C0C]',
          side === 'right' && 'sm:max-w-md',
          side === 'bottom' && 'max-h-[92vh] rounded-t-2xl'
        )}
      >
        <SheetHeader className="sticky top-0 z-10 space-y-0 border-b border-[#E5E7EB] bg-white px-5 py-4 pr-16 text-left">
          <SheetTitle className="text-[18px] font-extrabold leading-[1.2] text-[#0C0C0C]">
            Early Pickup
          </SheetTitle>
          <SheetDescription className="sr-only">
            Your trust record with Motito
          </SheetDescription>
          <p className="text-[12px] font-medium text-grey-neutral">
            Your trust record with Motito
          </p>
        </SheetHeader>

        <div className="flex flex-col px-5 py-5">
          {!isAuthenticated ? (
            <AnonymousState onBeforeNavigate={handleAnonBeforeNavigate} />
          ) : repScoreStore.isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="size-8 animate-spin rounded-full border-4 border-[#E5E7EB] border-t-motitoBlue" />
              <p className="mt-4 text-sm text-grey-neutral">Loading your trust record</p>
            </div>
          ) : repScoreStore.error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-sm text-grey-neutral">Unable to load your trust record</p>
              <button
                type="button"
                onClick={() => {
                  const userId = authStore.currentUser?.id || 'me';
                  void repScoreStore.loadUserRepScore(userId);
                }}
                className="mt-4 text-sm font-semibold text-motitoBlue hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <StateComponent onPayNow={handlePayNow} onStartPlan={handleStartPlan} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
});

RepScoreDrawer.displayName = 'RepScoreDrawer';

export default RepScoreDrawer;
